import { useAutoAnimate } from "@formkit/auto-animate/react"
import axios from "axios"
import clsx from "clsx"
import { useContext, useState } from "react"
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3"
import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import { ERRORTYPES, MAX_PRODUCT_PRICE } from "../../config"
import { getEnvConfig } from "../../env"
import { Context } from "../../store/store"
import Alert from "../elements/Alert"
import ElemInput from "../elements/Input"
import ElemSelect from "../elements/Select"
import Spinner from "../elements/Spinner"
import Receipt from "../receipt/Receipt"
import { getText } from "../strapi"
import PreFillForm from "./PreFillForm"
import Mafo from "./Mafo"
import Legal from "./Legal"

const Form = () => {
	const { globalState } = useContext(Context)
	const navigate = useNavigate()
	const [formError, setFormError] = useState<ERRORTYPES | null>(null)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [animationRef] = useAutoAnimate<HTMLDivElement>()

	const {
		register,
		handleSubmit,
		setError,
		setValue,
		watch,
		reset,
		formState: { errors },
	} = useForm({})

	function handleFormSubmit(data) {
		if (!globalState.receiptReference) {
			setFormError(ERRORTYPES.UPLOADSMISSING)
			return
		}

		setIsLoading(true)
		const env = getEnvConfig()

		axios
			.post(env.apibase + "order.php?cv=" + Date.now(), {
				...data,
				receiptDataEntries: globalState.receiptReference,
			})
			.then(res => {
				if (res.data.externalIdentifier) {
					try {
						// eslint-disable-next-line
						let dataLayer = window["dataLayer"] || []
						dataLayer.push({
							event: "form-submit",
							"form-name": "particpate",
							"form-submit": false,
							field_error: "send",
						})
					} catch (e) {}

					navigate("/danke")
				} else {
					setFormError(ERRORTYPES.SERVER)
					try {
						// eslint-disable-next-line
						let dataLayer = window["dataLayer"] || []
						dataLayer.push({
							event: "form-submit",
							"form-name": "participate",
							"form-submit": true,
							package: globalState.package,
						})
					} catch (e) {}
					scrollToError()
				}
			})
			.catch(error => {
				parseOrderErrors(error.response.data.errors)
				if (error.response.data.errors.errors) {
					setFormError(error.response.data.errors.errors.join(" "))
				} else {
					setFormError(ERRORTYPES.SERVER)
				}
				scrollToError()
			})
			.finally(() => {
				setIsLoading(false)
			})
	}

	function parseOrderErrors(errors) {
		for (const key in errors.children) {
			if (errors.children[key].errors && key) {
				setError(key, {
					type: "custom",
					message: errors.children[key].errors.join(""),
				})
			}
		}
	}

	function scrollToError() {
		setTimeout(() => {
			try {
				const doc: any = document
				const { top } = doc.querySelector(".js-formerror").getBoundingClientRect()

				window.scrollTo({
					top: top + window.scrollY - 120,
					left: 0,
					behavior: "smooth",
				})
			} catch (error) {}
		}, 500)
	}

	return (
		<form
			onSubmit={handleSubmit(handleFormSubmit)}
			noValidate={true}
			autoComplete="off"
			id="form"
			className=" bg-repeat-y bg-white mx-auto relative z-10 "
		>
			{process.env.NODE_ENV === "development" && <PreFillForm setValue={setValue} />}
			<GoogleReCaptchaProvider language="de" useRecaptchaNet={true} reCaptchaKey="6LfiICcpAAAAAFCqzjdKjsHpiKkptwfZpqk0qq1n">
				<div className="max-w-[1324px] mx-auto px-6 md:px-0" id="form-content">
					<Receipt />
					<h2 className="pt-6 sm:pt-11 sm:pb-1 text-blue uppercase text-left">Formular ausfüllen</h2>
					<p className="max-w-[950px] text-left font-AmsiRegular  text-blue mb-12">
						Fordere ganz einfach die Rückerstattung Deines Kaufpreises an. Fülle dazu die folgenden Felder vollständig aus und sende das Formular ab. Nach Prüfung Deiner Angaben erstatten wir Dir den Betrag auf das angegebene Konto.
					</p>
					<p className="text-[22px] text-blue"> Produkt- und Kaufangaben</p>
					<section className="pt-6 mx-auto grid max-w-full grid-cols-1 sm:grid-cols-2 gap-4  sm:gap-5">
						
							<div className="inputElemSelectSizeWrap  ">
							<ElemSelect label={"Gekauftes Produkt*"} hasError={errors.product1 ? true : false} errMsg={"Bitte Produkt wählen."}>
								<select
									{...register("product1", {
										required: true,
										minLength: 1,
									})}
									className={"inputElemSelect" + (errors.product1 ? "  js-formerror border-[#E41D1D] border-[2px]" : "")}
								>
									<option disabled selected value="" className="text-lightgray">
										bitte wählen
									</option>

									<option value="a">TREK Protein Oat Bars Biscoff</option>
									<option value="b">TREK Protein Oat Bars Cocoa</option>
								</select>
							</ElemSelect>
						</div>
						<div>
							<ElemInput
								label={
									<>
										Kaufpreis*
									</>
								}
								value={watch("purchaseAmount", false)}
								hasError={errors.purchaseAmount}
								errMsg={"Bitte Kaufbetrag eingeben. (max. 3,99 EUR)"}
							>
								<input
									{...register("purchaseAmount", {
										required: true,
										min: "0.1",
										max: MAX_PRODUCT_PRICE,
									})}
									type="number"
									step="0.01"
									min="0.1"
									max={MAX_PRODUCT_PRICE}
									autoComplete="off"
									className={clsx("peer inputElem", errors.purchaseAmount && "inputElemErr js-formerror border-[#E41D1D] border-[2px]")}
								/>
							</ElemInput>
						</div>
						</section>
						<p className="mt-12 mb-2 text-[22px] text-blue">Persönliche Angaben</p>
						<section className="pt-6 mx-auto grid max-w-full grid-cols-1 sm:grid-cols-2 gap-4  sm:gap-5">
						<div className="inputElemSelectSizeWrap sm:col-span-2">
							<ElemSelect label={""} hasError={errors.salutation ? true : false} className="col-span-2 max-w-[200px]" errMsg={"Bitte Vornamen eingeben."}>
								<select
									{...register("salutation", {
										required: true,
										minLength: 1,
									})}
									className={" inputElemSelect" + (errors.salutation ? "  js-formerror border-[#E41D1D] border-[2px]" : "")}
								>
									<option className="4" selected value="4">
										Anrede
									</option>
									<option value="1">Herr</option>
									<option value="2">Frau</option>
									<option value="3">Divers</option>
								</select>
							</ElemSelect>
						</div>
						<ElemInput label={"Vorname*"} value={watch("firstName", false)} hasError={errors.firstName} errMsg={"Bitte Vornamen eingeben."}>
							<input
								{...register("firstName", {
									required: true,
									minLength: 1,
								})}
								type="text"
								minLength={1}
								maxLength={30}
								autoComplete="off"
								className={clsx("peer inputElem", errors.firstName && "inputElemErr js-formerror border-[#E41D1D] border-[2px]")}
							/>
						</ElemInput>
						<ElemInput label={"Nachname*"} value={watch("lastName", false)} hasError={errors.lastName} errMsg={"Bitte Nachnamen eingeben."}>
							<input
								{...register("lastName", {
									required: true,
									minLength: 1,
									maxLength: 30,
								})}
								type="text"
								minLength={1}
								maxLength={30}
								autoComplete="off"
								className={clsx("peer inputElem", errors.lastName && "inputElemErr js-formerror border-[#E41D1D] border-[2px]")}
							/>
						</ElemInput>
						<ElemInput label={"E-Mail-Adresse*"} value={watch("email", false)} hasError={errors.email} errMsg={"Bitte E-Mail-Adresse eingeben."}>
							<input
								{...register("email", {
									required: true,
									minLength: 1,
									maxLength: 100,
									pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
								})}
								type="text"
								minLength={1}
								maxLength={100}
								autoComplete="off"
								className={clsx("peer inputElem", errors.email && "inputElemErr js-formerror border-[#E41D1D] border-[2px]")}
							/>
						</ElemInput>
						<ElemInput label={"Geburtsdatum*"} value={watch("birthdate", false)} hasError={errors.birthdate} errMsg={"Eine Teilnahme ist erst ab 18 Jahren möglich."}>
							<input
								{...register("birthdate", {
									required: true,
									validate: value => {
										const today = new Date()
										const birthdate = new Date(value)
										let age = today.getFullYear() - birthdate.getFullYear()
										const m = today.getMonth() - birthdate.getMonth()
										if (m < 0 || (m === 0 && today.getDate() < birthdate.getDate())) {
											age--
										}
										return age >= 18
									},
								})}
								type="date"
								min="1900-09-06"
								max="2027-09-06"
								autoComplete="off"
								className={clsx("peer inputElem  ", errors.birthdate && "inputElemErr js-formerror border-[#E41D1D] border-[2px]")}
							/>
						</ElemInput>

						<ElemInput label={"Kontoinhaber*"} value={watch("accountHolder", false)} hasError={errors.accountHolder} errMsg={"Bitte Kontoinhaber angeben."}>
							<input
								{...register("accountHolder", {
									required: true,
									minLength: 1,
									maxLength: 30,
									pattern: /^[a-zA-ZäöüÄÖÜß\s\-]+$/,
								})}
								type="text"
								minLength={1}
								maxLength={30}
								autoComplete="off"
								onKeyDown={e => { if (!/[a-zA-ZäöüÄÖÜß\s\-]/.test(e.key) && e.key.length === 1) e.preventDefault() }}
								className={clsx("peer inputElem", errors.accountHolder && "inputElemErr js-formerror border-[#E41D1D] border-[2px]")}
							/>
						</ElemInput>
						<ElemInput label={"IBAN*"} value={watch("iban", false)} hasError={errors.iban} errMsg={"Bitte IBAN angeben. "}>
							<input
								{...register("iban", {
									required: true,
									minLength: 16,
									maxLength: 31,
								})}
								type="text"
								minLength={16}
								maxLength={31}
								autoComplete="off"
								className={clsx("peer inputElem", errors.iban && "inputElemErr js-formerror border-[#E41D1D] border-[2px]")}
							/>
						</ElemInput>
						{watch("iban", false) && watch("iban", false).length > 2 && watch("iban", false).substring(0, 2).toUpperCase() !== "DE" && (
							<ElemInput label={"BIC*"} value={watch("bic", false)} hasError={errors.bic} errMsg={"Bitte BIC angeben. "}>
								<input
									{...register("bic", {
										required: true,
										minLength: 8,
										maxLength: 11,
									})}
									type="text"
									minLength={8}
									maxLength={11}
									autoComplete="off"
									className={clsx("peer inputElem", errors.bic && "inputElemErr js-formerror border-[#E41D1D] border-[2px]")}
								/>
							</ElemInput>
						)}
					</section>
						<Legal {...{ register, errors }} />
							<p className="font-AmsiRegular text-blue text-[14px] mt-12 md:mt-24 sm:text-[16px] mb-4">* Pflichtangaben</p>
					</div>
					<div className="bg-bottom-divier bg-white bg-[url('/src/bgimages/bg-divider-desktop.png')] bg-no-repeat w-full bg-center bg-cover h-[100px] md:h-[300px]"></div>
						<div className="bg-[#002F42]/5 pb-12 md:pb-24">
							<Mafo {...{ register, errors, watch, setValue }} />
						</div>
						<div className="pt-8 pb-6 sm:pb-2 max-w-[876] mx-auto bg-blue/5 pb-12 relative z-[12]" ref={animationRef}>
							<div className="relative px-4">
								{formError && <Alert className={"my-4 font-bold text-errorred"}>{formError}</Alert>}
								<div className="relative text-center mb-12 md:mb-0">
									<button
										type="submit"
										className=" btn-primary-yellow px-10"
										disabled={isLoading ? true : undefined}
									>
										{isLoading && <Spinner />}
										Absenden
									</button>
								</div>
							</div>
						</div>
						<div className="bg-bottom-divier social-divider bg-[url('/src/bgimages/bg-bottom-divider.png')] bg-no-repeat w-full bg-center bg-cover h-[100px] md:h-[300px]"></div>
						<div className="social- footer bg-blue">
							<div className="max-w-[1324px] mx-auto px-6 md:px-0 pb-12 footer-social">
								<img src={process.env.PUBLIC_URL + "/images/footer-img-mobile.png"} alt="TREK Logo" className="mx-auto -translate-y-[10rem] md:hidden" />
								<h2 className="text-white hidden md:block">FOLGE UNS AUF SOCIAL MEDIA UND <br/> WERDE TEIL UNSERER COMMUNITY</h2>
								<h2 className="text-white md:hidden max-w-[75%] mx-auto pt-24 -mt-[15rem]">FOLGE UNS AUF SOCIAL MEDIA</h2>
								<a href="https://www.instagram.com/trek.bars" target="_blank" rel="noopener noreferrer"><p className="text-center"><img src={process.env.PUBLIC_URL + "/images/insta.svg"} alt="TREK auf Instagram"  className="mx-auto"/></p></a>
								<div className="flex flex-wrap justify-evenly gap-4  max-w-[80%] md:max-w-[60%] mx-auto mt-6 md:mt-24">
									<Link to="/faq" rel="noopener noreferrer" className="text-white text-center font-AmsiRegular text-[22px]">
									FAQ
									</Link>
									<Link to="/teilnahmebedingungen" rel="noopener noreferrer" className="text-white text-center font-AmsiRegular text-[22px]">
									Teilnahmebedingungen
									</Link>
									<a href="https://www.trekbars.com/de/datenschutzbestimmungen" target="_blank" rel="noopener noreferrer" className="text-white text-center font-AmsiRegular text-[22px]">
									Datenschutz
									</a>
									<Link to="/kontakt"  rel="noopener noreferrer" className="text-white text-center font-AmsiRegular text-[22px]">
									Kontakt
									</Link>
									<Link to="/impressum"  rel="noopener noreferrer" className="text-white text-center font-AmsiRegular text-[22px]">
									Impressum
									</Link>
									</div>
							</div>
						</div>
			</GoogleReCaptchaProvider>
		</form>
	)
}
	
export default Form
